import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useContext, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import type { MenuItem, MenuPosition, Section } from '../../../../../types';
import { useDeepCompareEffect } from '../../../../helpers';

type DataContextType = {
  metadata: Partial<MenuItem>;
  visibleSections: Section[];
  selectedSection: Section | null;
  itemsBySections: Record<string, MenuPosition[]>;
  setSelectedSection: Dispatch<SetStateAction<Section | null>>;
};

export const DataContext = createContext<DataContextType | null>(null);
export const useData = () => {
  const context = useContext(DataContext);

  if (context === null) {
    throw new Error(`DataContext should be used within DataProvider`);
  }

  return context;
};

export function DataProvider({
  data,
  children
}: PropsWithChildren<{ data: MenuItem }>) {
  const { t } = useLingui();
  const { items = [], sectionsEnabled, sections: menuSections } = data;
  const [metadata, setMetadata] = useState<Partial<MenuItem>>({
    name: '',
    icon: { url: '' },
    caption: '',
    footerText: '',
    sectionsEnabled: false
  });
  const [visibleSections, setVisibleSections] = useState<Section[]>([]);
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [itemsBySections, setItemsBySections] = useState<
    Record<string, MenuPosition[]>
  >({});

  const uncategorizedText = t`Uncategorized`;

  const providerValue = {
    metadata,
    visibleSections,
    selectedSection,
    itemsBySections,
    setSelectedSection
  };

  useDeepCompareEffect(() => {
    const noSections = [
      {
        name: uncategorizedText,
        id: 'uncategorized'
      }
    ];

    const isMenuSectionsNotEmpty = !!menuSections && menuSections.length > 0;
    const menuSectionsWithNull = isMenuSectionsNotEmpty && [
      ...menuSections,
      { name: '', id: null }
    ];

    // тут добавляем секции к позициям меню, если есть
    const realItems = isMenuSectionsNotEmpty
      ? items.map((item) => {
          const isSectionExist =
            menuSectionsWithNull &&
            menuSectionsWithNull.some(
              (section) => section.id === (item.section || null)
            );

          return isSectionExist ? item : { ...item, section: null };
        })
      : items;

    const processedSections =
      isMenuSectionsNotEmpty &&
      menuSectionsWithNull &&
      menuSectionsWithNull
        .filter((item) =>
          realItems?.find(
            (itemsItem) => (itemsItem.section || null) === item.id
          )
        )
        .map((item) => {
          if (!item.id) {
            return {
              name: uncategorizedText,
              id: 'uncategorized'
            };
          }

          return item;
        });

    const sections =
      (!sectionsEnabled && noSections) || processedSections || noSections;

    const finalItemsSorted = sections.reduce(
      (prevValue: Record<string, MenuPosition[]>, currentValue) => {
        const result = prevValue;

        if (currentValue.id) {
          const arraySectionItems = items?.filter(
            (resultItem) =>
              resultItem.visible !== false &&
              resultItem.section === currentValue.id
          );
          if (arraySectionItems.length) {
            result[currentValue.id] = arraySectionItems;
          }
        }

        const arrayUncategorizedItems = items?.filter(
          (resultItem) =>
            resultItem.visible !== false &&
            (!resultItem.section ||
              resultItem.section === 'uncategorized' ||
              !sectionsEnabled ||
              !sections.some((section) => section.id === resultItem.section))
        );

        if (arrayUncategorizedItems.length) {
          result.uncategorized = arrayUncategorizedItems;
        }

        return result;
      },
      {}
    );
    setItemsBySections(finalItemsSorted);

    const keysItemsSorted = Object.keys(finalItemsSorted);
    const visibleSections = sections.filter((el) =>
      keysItemsSorted.includes(el.id)
    );
    setVisibleSections(visibleSections as Section[]);
    setSelectedSection(visibleSections[0] as Section);

    setMetadata({
      id: data.id,
      name: data.name,
      icon: data.icon,
      caption: data.caption,
      footerText: data.footerText,
      sectionsEnabled: data.sectionsEnabled
    });
  }, [data, uncategorizedText]);

  return (
    <DataContext.Provider value={providerValue}>
      {children}
    </DataContext.Provider>
  );
}
